import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  uniqueIndex,
  date,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { carriers } from "./business";
import { clients } from "./business";

export enum CommissionCalculationType {
  PERCENTAGE = "percentage",
  FLAT = "flat",
  PEPM = "pepm",
  PMPM = "pmpm",
}

// Import + re-export from planTypes.ts to break circular dep: plans → business → financial → plans
import { PlanType, normalizePlanType, isLifeDisabilityType, requiresBeneficiary } from "./planTypes";
export { PlanType, normalizePlanType, isLifeDisabilityType, requiresBeneficiary };

export function isSupplementalType(planType: string | undefined | null): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return ["accident", "critical_illness"].includes(normalized);
}

export function isFSAType(planType: string | undefined | null): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return [
    "fsa_health",         // current PlanType.FSA_HEALTH
    "fsa_dependent_care", // current PlanType.FSA_DEPENDENT_CARE
    "health_care_fsa",    // legacy — kept defensively in case any rows still use this
    "dependent_care_fsa", // legacy
  ].includes(normalized);
}

export function needsNetworkConfig(planType: string | undefined | null): boolean {
  if (!planType) return true;
  const normalized = planType.toLowerCase().trim();
  return ["health", "medical", "dental", "vision"].includes(normalized);
}

/**
 * Checks if a plan type is a health/medical plan
 * @param planType - The plan type to check
 * @returns true if the plan type is health or medical
 */
export function isHealthOrMedicalPlan(planType: string | undefined | null): boolean {
  if (!planType) return false;
  const normalized = planType.toLowerCase().trim();
  return normalized === "health" || normalized === "medical";
}

export enum NetworkType {
  HMO = "HMO",
  PPO = "PPO",
  EPO = "EPO",
  POS = "POS",
  HDHP = "HDHP",
}

export enum MetalTier {
  BRONZE = "Bronze",
  SILVER = "Silver",
  GOLD = "Gold",
  PLATINUM = "Platinum",
}

export enum WaitingPeriodType {
  DAYS = "days",
  MONTHS = "months",
}

export enum ChildrenTermination {
  IMMEDIATE = "immediate",
  END_OF_MONTH = "end_of_month",
  END_OF_CALENDAR_YEAR = "end_of_calendar_year",
}

// Termination timing for employee status changes (FT→PT)
export enum TerminationTiming {
  IMMEDIATE = "immediate",         // Same day as status change
  MID_MONTH = "mid_month",         // 15th of the month
  END_OF_MONTH = "end_of_month",   // Last day of month
}

export enum EntryDate {
  IMMEDIATE = "immediate",
  FIRST_OF_MONTH = "first_of_month",
  FIRST_OF_FOLLOWING_MONTH = "first_of_following_month",
  FIFTEENTH_OF_MONTH = "15th_of_month",
}

export enum TaxDetermination {
  PRE_TAX = "pre-tax",
  POST_TAX = "post-tax",
}

export enum EmploymentType {
  FULL_TIME = "full-time",
  PART_TIME = "part-time",
  VARIABLE_HOUR = "variable-hour",
  SEASONAL = "seasonal",
  CONTRACTOR = "contractor",
  INTERN = "intern",
}

export enum ContingencyType {
  ENROLLED = "enrolled",
  ENROLLED_BUNDLED = "enrolled-bundled",
  ENROLLED_BUNDLED_COBRA = "enrolled-bundled-cobra",
  NOT_ENROLLED = "not-enrolled",
}

// Plan Benefits Enums
export enum BenefitType {
  MEDICAL = "medical",
  PRESCRIPTION = "prescription",
  PREVENTIVE = "preventive",
  EMERGENCY = "emergency",
  SPECIALIST = "specialist",
  URGENT_CARE = "urgent_care",
  HOSPITAL = "hospital",
  MENTAL_HEALTH = "mental_health",
  SUBSTANCE_ABUSE = "substance_abuse",
  MATERNITY = "maternity",
  DENTAL_BASIC = "dental_basic",
  DENTAL_MAJOR = "dental_major",
  ORTHODONTIA = "orthodontia",
  VISION_EXAM = "vision_exam",
  VISION_FRAMES = "vision_frames",
  VISION_LENSES = "vision_lenses",
  // Life Insurance Benefits
  LIFE_BASIC = "life_basic",
  LIFE_VOLUNTARY = "life_voluntary",
  LIFE_SPOUSE = "life_spouse",
  LIFE_CHILD = "life_child",
  ACCIDENTAL_DEATH = "accidental_death",
  // Disability Insurance Benefits
  SHORT_TERM_DISABILITY = "short_term_disability",
  LONG_TERM_DISABILITY = "long_term_disability",
}

// Life Insurance Specific Enums
export enum CoverageAmountType {
  FLAT_AMOUNT = "flat_amount", // Fixed dollar amount (e.g., $100,000)
  EARNINGS_MULTIPLE = "earnings_multiple", // Multiple of salary (e.g., 2x, 3x)
  EARNINGS_PERCENTAGE = "earnings_percentage", // Percentage of salary (e.g., 60%)
}

export enum EarningsBase {
  BASE_SALARY = "base_salary", // Base salary only
  BASE_PLUS_BONUS = "base_plus_bonus", // Base + annual bonus
  BASE_PLUS_COMMISSION = "base_plus_commission", // Base + commission
  TOTAL_COMPENSATION = "total_compensation", // All compensation
  W2_EARNINGS = "w2_earnings", // W2 reportable earnings
}

export enum RoundingRule {
  NONE = "none", // No rounding
  NEAREST_THOUSAND = "nearest_thousand", // Round to nearest $1,000
  NEAREST_TEN_THOUSAND = "nearest_ten_thousand", // Round to nearest $10,000
  ROUND_UP_THOUSAND = "round_up_thousand", // Round up to next $1,000
  ROUND_DOWN_THOUSAND = "round_down_thousand", // Round down to previous $1,000
  ROUND_UP_TEN_THOUSAND = "round_up_ten_thousand", // Round up to next $10,000
  ROUND_DOWN_TEN_THOUSAND = "round_down_ten_thousand", // Round down to previous $10,000
  NEAREST_TEN = "nearest_ten", // Round to nearest $10
  NEAREST_HUNDRED = "nearest_hundred", // For disability (payroll increments)
  CUSTOM = "custom", // Custom rounding amount
}

export enum EOIStatus {
  NOT_REQUIRED = "not_required", // Coverage under GI limit
  PENDING = "pending", // Awaiting EOI submission
  SUBMITTED = "submitted", // EOI submitted to carrier
  APPROVED = "approved", // EOI approved
  DENIED = "denied", // EOI denied
  CONDITIONALLY_APPROVED = "conditionally_approved", // Approved with exclusions
}

export enum EligibilityWindow {
  NEWLY_ELIGIBLE = "newly_eligible", // Initial enrollment period
  OPEN_ENROLLMENT = "open_enrollment", // Annual OE period
  QUALIFYING_EVENT = "qualifying_event", // Life event change
  LATE_ENTRANT = "late_entrant", // Enrolling outside normal windows
}

// Disability Insurance Specific Enums
export enum DisabilityBenefitPeriod {
  THIRTEEN_WEEKS = "13_weeks",
  TWENTY_SIX_WEEKS = "26_weeks",
  TWO_YEARS = "2_years",
  FIVE_YEARS = "5_years",
  TEN_YEARS = "10_years",
  TO_AGE_65 = "to_age_65",
  TO_AGE_67 = "to_age_67",
  TO_SOCIAL_SECURITY_RETIREMENT = "to_ss_retirement",
  TO_SSNRA = "to_ssnra",
  LIFETIME = "lifetime",
}

export enum DisabilityDefinition {
  OWN_OCCUPATION = "own_occupation", // Cannot perform own job
  MODIFIED_OWN_OCCUPATION = "modified_own_occupation", // Own job for period, then any
  ANY_OCCUPATION = "any_occupation", // Cannot perform any job
}

export enum EliminationPeriod {
  ZERO_DAYS = "0_days", // Immediate coverage
  SEVEN_DAYS = "7_days",
  FOURTEEN_DAYS = "14_days",
  THIRTY_DAYS = "30_days",
  SIXTY_DAYS = "60_days",
  NINETY_DAYS = "90_days",
  ONE_HUNDRED_EIGHTY_DAYS = "180_days",
}

// NEW CLEAN RATE STRUCTURE ENUMS
export enum RateStructureType {
  TIER_RATES = "tier_rates", // Fixed pricing by coverage tier
  AGE_RATES = "age_rates", // Variable pricing by age bands
  AGE_TIER_RATES = "age_tier_rates", // Age band + tier matrix rates
  TABLE_RATES = "table_rates", // Coverage table method (matrix of coverages)
  WB_TABLE = "wb_table", // Word & Brown imported age table
}

export enum CoverageTier {
  EMPLOYEE_ONLY = "employee_only",
  EMPLOYEE_SPOUSE = "employee_spouse",
  EMPLOYEE_CHILDREN = "employee_children",
  FAMILY = "family",
}

export enum AgeBandType {
  ALL_AGES = "all_ages",
  FIVE_YEAR = "five_year",
  TEN_YEAR = "ten_year",
}

// Plan Features Enums
export enum PlanFeatureType {
  WELLNESS = "wellness",
  HSA_ELIGIBLE = "hsa_eligible",
  TELEHEALTH = "telehealth",
  PRESCRIPTION_DRUG = "prescription_drug",
  PREVENTIVE_CARE = "preventive_care",
  MENTAL_HEALTH = "mental_health",
  MATERNITY = "maternity",
  VISION = "vision",
  DENTAL = "dental",
  CHIROPRACTIC = "chiropractic",
  ACUPUNCTURE = "acupuncture",
}

export const plans = pgTable(
  "plans",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    adminName: text("admin_name"),
    type: text("type").notNull(), // health, dental, vision, life, disability, voluntary
    carrierId: integer("carrier_id").notNull().references(() => carriers.id, { onDelete: "restrict" }),
    clientId: integer("client_id").references(() => clients.id),
    agencyId: text("agency_id"), // UUID of the agency that owns this plan
    planCode: text("plan_code").notNull(), // unique carrier plan identifier
    subGroupNumber: text("sub_group_number"),
    networkType: text("network_type"), // HMO, PPO, EPO, POS, HDHP
    // Carrier's named provider network (e.g. "Select HMO", "California Care
    // HMO") — distinguishes same-named plans on different networks. Free text;
    // the Plan Builder suggests values already used for the carrier.
    network: text("network"),
    metalTier: text("metal_tier"), // Bronze, Silver, Gold, Platinum for ACA plans
    // ACA reporting facts about the plan (superadmin/plan library owns these):
    // funding drives 1095-C Part III (self/level-funded => covered individuals),
    // MV/MEC drive Line 14. minimumValue NULL => derived from metalTier
    // (any metal tier => at least 60% AV => MV).
    fundingType: text("funding_type").default("fully_insured"), // fully_insured | self_insured | level_funded
    minimumValue: boolean("minimum_value"),
    minimumEssentialCoverage: boolean("minimum_essential_coverage").default(true),
    description: text("description"),
    isActive: boolean("is_active").default(true),
    commissionRate: decimal("commission_rate", {
      precision: 5,
      scale: 2,
    }).notNull(), // percentage
    commissionCalculationType: text("commission_calculation_type")
      .default(CommissionCalculationType.PERCENTAGE)
      .notNull(),
    commissionValue: decimal("commission_value", {
      precision: 10,
      scale: 2,
    }),
    renewalMonth: integer("renewal_month").default(1), // 1-12 for month of year
    renewalDate: timestamp("renewal_date"), // one year from effective date (same day)
    maxAge: integer("max_age"), // maximum age for coverage
    minAge: integer("min_age"), // minimum age for coverage
    waitingPeriod: integer("waiting_period").default(0), // waiting period duration
    waitingPeriodType: text("waiting_period_type").default("days"), // days or months
    effectiveDate: timestamp("effective_date"), // when the plan becomes active
    endDate: timestamp("end_date"), // when the plan expires (typically one year from effective date)
    availableStartDate: timestamp("available_start_date"),
    availableEndDate: timestamp("available_end_date"),
    // Rate structure type for convenience on plan header (optional)
    rateStructureType: text("rate_structure_type"), // tier_rates or table_rates
    // Dependent age requirements
    spouseMinAge: integer("spouse_min_age").default(18),
    spouseMaxAge: integer("spouse_max_age").default(65),
    childrenMinAge: integer("children_min_age").default(0),
    childrenMaxAge: integer("children_max_age").default(26),
    childrenStudentMaxAge: integer("children_student_max_age").default(26),
    childrenDisabledMaxAge: integer("children_disabled_max_age"), // null means no limit
    childrenTermination: text("children_termination").default("end_of_month"), // immediate, end_of_month, end_of_calendar_year
    // Dependent eligibility flags
    spouseEligible: boolean("spouse_eligible").default(true),
    childrenEligible: boolean("children_eligible").default(true),
    entryDate: text("entry_date").default("immediate"), // immediate, first_of_month, first_of_following_month, 15th_of_month
    terminationTiming: text("termination_timing").default("end_of_month"), // immediate, mid_month, end_of_month - when benefits end on status change
    taxDetermination: text("tax_determination").default("pre-tax"), // pre-tax, post-tax
    domesticPartnerTaxDetermination: text(
      "domestic_partner_tax_determination"
    ).default("pre-tax"), // pre-tax, post-tax
    ageDetermination: text("age_determination").default("plan_anniversary"), // plan_anniversary, plan_anniversary_ignore_hire, first_of_month, end_of_month, first_of_year
    employmentTypes: text("employment_types").array().default(["full-time"]), // full-time, part-time, variable-hour, seasonal, contractor, intern
    contingencyType: text("contingency_type").default("enrolled"), // enrolled, enrolled-bundled, enrolled-bundled-cobra, not-enrolled
    contingencyPlanId: integer("contingency_plan_id"), // reference to another plan
    renewedFromPlanId: integer("renewed_from_plan_id"), // when renewed, points to the source plan.id
    needsRateReview: boolean("needs_rate_review").default(false), // flag for renewed plans that need rate verification
    // Plan Library v2 (docs/PLAN_RATING_ACCEPTANCE_SPEC.md).
    // "employer-location": ACA fully-insured — group rating area derived from the
    // employer's primary location, fixed for the plan year (all library plans).
    // "member-location": reserved designation for broker-built self/level-funded
    // plans; rating mechanics deferred (spec scenario 13).
    ratingBasis: text("rating_basis").default("employer-location"),
  // Broker-created library plans can be private to one employer: the plan
  // stays in the library (clientId NULL, linked via client_plans) but library
  // pickers only offer it when browsing for this client. Null = agency-wide.
  visibleToClientId: integer("visible_to_client_id").references(() => clients.id, {
    onDelete: "set null",
  }),
    // Mid-year age re-determination trigger extending ageDetermination (spec
    // scenario 19): none (ACA default — age locked for the plan year),
    // birthday_month, policy_anniversary.
    midYearAgeRedetermination: text("mid_year_age_redetermination").default("none"),
    // Library plan retirement (spec scenario 07): blocks new client links;
    // existing links keep resolving their locked version through their plan year.
    retiredAt: timestamp("retired_at"),
    enrollmentRequired: boolean("enrollment_required").default(false), // employee cannot waive this plan
    availableStates: text("available_states").array(), // states where this plan is available (e.g. ["CA","NY"])
    // Word & Brown library link — set on migration import (Path B) and on
    // future quoting flows that pick a plan from the W&B catalog. Lets
    // downstream tooling (quoting, renewal, rate refresh) treat the plan as
    // first-class against the platform's rate library.
    wordBrownId: text("word_brown_id"),
    // Self-link to a system/library plan (clientId null). Set when a broker
    // manually creates a plan and matches it to a library entry, so form
    // mappings keyed on the library plan's id check the right box at enrollment.
    systemPlanId: integer("system_plan_id"),
    // Origin of a library plan (clientId null): "manual" (built in the Plan
    // Library UI) or an external source such as "word_brown" (populated by the
    // daily catalog sync). Lets the Plan Library separate curated vs synced
    // plans and drives the "never call the API at runtime" rule.
    source: text("source").default("manual"),
    // When this plan was last refreshed by an external-source sync (null for manual).
    lastSyncedAt: timestamp("last_synced_at"),
    // Raw Word & Brown benefit lines exactly as the carrier returned them.
    // Structured `planBenefits` rows are the editable/curated view; this is the
    // lossless safety net so W&B benefit lines that map to no structured field
    // (e.g. "No Charge", "$15 / $50") are never dropped on import.
    rawBenefits: jsonb("raw_benefits").$type<Array<{
      name: string;
      value: string;
      network?: "in" | "out";
      deductibleApply?: boolean;
    }>>(),
    // Plan Library two-person review (DEV-388, migration 0147): draft |
    // needs_review | approved | changes_requested. NULL = not under review —
    // every broker/agency/client plan, plus library rows created by broker
    // flows. Only Approved (or NULL) library plans are quotable/linkable/
    // enrollable; see shared/utils/planReview.ts.
    reviewStatus: text("review_status"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    carrierReference: index("plans_carrier_idx").on(table.carrierId),
    clientReference: index("plans_client_idx").on(table.clientId),
    agencyReference: index("plans_agency_idx").on(table.agencyId),
    wordBrownIdReference: index("plans_word_brown_id_idx").on(table.wordBrownId),
    systemPlanReference: index("plans_system_plan_idx").on(table.systemPlanId),
    sourceReference: index("plans_source_idx").on(table.source),
  })
);

// Unified Plan Rates table (combines structure + rows)
export const planRates = pgTable(
  "plan_rates",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    clientId: integer("client_id").references(() => clients.id, { onDelete: "set null" }),
    rateStructureType: text("rate_structure_type").notNull(), // tier_rates / age_rates / table_rates / wb_table
    ageBandType: text("age_band_type"), // for age_rates: all_ages, five_year, ten_year
    coverageTier: text("coverage_tier"), // for tier rates
    ageBand: text("age_band"), // for age rates
    coverageTable: text("coverage_table"), // for coverage table method
    // Rating Area metadata (optional). For CA, ratingAreaId is 1..19, label like "AREA 12".
    ratingAreaId: integer("rating_area_id"),
    ratingAreaLabel: text("rating_area_label"),
    // Optional origin for audit (e.g., wb_api, manual)
    origin: text("origin"),
    monthlyRate: decimal("monthly_rate", { precision: 10, scale: 2 }).notNull(),
    tobaccoMonthlyRate: decimal("tobacco_monthly_rate", { precision: 10, scale: 2 }),
    // Effective-dated versioning (migration 0093). A "version" = the rows sharing
    // (planId, clientId, ratingAreaId, effectiveStart). Null start = original/
    // legacy rows (effective from forever); null end = current/open-ended. See
    // shared/utils/planRateResolution.ts for how a version is selected per period.
    effectiveStart: date("effective_start"),
    effectiveEnd: date("effective_end"),
    // Version metadata link (migration 0094) — implicit FK -> plan_rate_versions.id.
    // Null = legacy rows resolved by effectiveStart alone.
    versionId: integer("version_id"),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    planReference: index("plan_rates_plan_idx").on(table.planId),
    clientReference: index("plan_rates_client_idx").on(table.clientId),
    activeReference: index("plan_rates_active_idx").on(table.isActive),
    ratingAreaReference: index("plan_rates_area_idx").on(table.ratingAreaId),
    versionReference: index("plan_rates_version_idx").on(
      table.planId,
      table.clientId,
      table.ratingAreaId,
      table.effectiveStart
    ),
  })
);

// Version metadata for plan_rates (Plan Library v2, migration 0094).
// A version is a window of PLAN-YEAR STARTS: it applies to plan years BEGINNING
// inside [effectiveStart, effectiveEnd) — the service month never selects the
// version; the employer's plan-year start does (spec scenarios 10/15). A
// correction (spec scenario 21) inserts a new row with the same effectiveStart
// and revision + 1, linked via correctionOfId; resolution picks the highest
// revision whose window covers the plan-year start. plan_rates rows link via
// plan_rates.version_id (null = legacy rows resolved by effectiveStart alone).
export const planRateVersions = pgTable(
  "plan_rate_versions",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    clientId: integer("client_id").references(() => clients.id, { onDelete: "cascade" }),
    effectiveStart: date("effective_start").notNull(),
    effectiveEnd: date("effective_end"),
    revision: integer("revision").notNull().default(1),
    correctionOfId: integer("correction_of_id"), // implicit self-FK -> plan_rate_versions.id
    correctionReason: text("correction_reason"),
    label: text("label"), // display only, e.g. "v1", "v1.1"
    publishedAt: timestamp("published_at").defaultNow(),
    createdById: varchar("created_by_id"), // users.id (varchar UUID)
    // Rate cadence (migration 0105) — implicit FK -> plan_rate_years.id, plus
    // this version's 1-based slot in that year's cadence. Null = legacy /
    // unmanaged version: resolves identically, but claims no completeness.
    rateYearId: integer("rate_year_id"),
    periodIndex: integer("period_index"),
  },
  (table) => ({
    planWindowIdx: index("plan_rate_versions_window_idx").on(
      table.planId,
      table.clientId,
      table.effectiveStart,
      table.revision
    ),
    rateYearIdx: index("plan_rate_versions_rate_year_idx").on(table.rateYearId),
  })
);

// Rate cadence declaration for one plan year (migration 0105). A carrier may
// file one set of rates per year (annual), two (biannual) or four (quarterly).
// Each set is an ordinary planRateVersions row — resolution is UNCHANGED; this
// table only fixes where the period boundaries fall (quarterly = Jan/Apr/Jul/
// Oct, biannual = Jan/Jul) and makes an incomplete year rejectable instead of
// silently leaving a gap the prior period swallows. Cadence is per YEAR, not
// per plan: a plan can be annual in 2026 and quarterly in 2027.
export const planRateYears = pgTable(
  "plan_rate_years",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    clientId: integer("client_id").references(() => clients.id, { onDelete: "cascade" }),
    year: integer("year").notNull(),
    cadence: text("cadence").notNull(), // annual | biannual | quarterly
    createdAt: timestamp("created_at").defaultNow(),
    createdById: varchar("created_by_id"), // users.id (varchar UUID)
  },
  (table) => ({
    planYearIdx: index("plan_rate_years_plan_idx").on(table.planId, table.year),
  })
);

// Age band definitions for table rates
export const ageBands = pgTable(
  "age_bands",
  {
    id: serial("id").primaryKey(),
    ageBandType: text("age_band_type").notNull(), // all_ages, five_year, ten_year
    ageBand: text("age_band").notNull(), // e.g., "25", "30-34", "40-49"
    minAge: integer("min_age").notNull(),
    maxAge: integer("max_age"), // null for "64+" type bands
    sortOrder: integer("sort_order").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    typeReference: index("age_bands_type_idx").on(table.ageBandType),
  })
);

// Plan Benefits and Coverage Details
export const planBenefits = pgTable("plan_benefits", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
  benefitType: text("benefit_type").notNull(), // medical, prescription, preventive, emergency, etc.
  inNetworkDeductible: decimal("in_network_deductible", {
    precision: 10,
    scale: 2,
  }),
  outNetworkDeductible: decimal("out_network_deductible", {
    precision: 10,
    scale: 2,
  }),
  inNetworkCoinsurance: decimal("in_network_coinsurance", {
    precision: 5,
    scale: 2,
  }), // percentage
  outNetworkCoinsurance: decimal("out_network_coinsurance", {
    precision: 5,
    scale: 2,
  }), // percentage
  inNetworkCopay: decimal("in_network_copay", { precision: 10, scale: 2 }),
  outNetworkCopay: decimal("out_network_copay", { precision: 10, scale: 2 }),
  maxOutOfPocket: decimal("max_out_of_pocket", { precision: 10, scale: 2 }),
  annualMaximum: decimal("annual_maximum", { precision: 10, scale: 2 }), // for dental/vision
  deductibleApply: boolean("deductible_apply").default(false), // whether deductible must be met before this benefit pays
  description: text("description"),
});

// Contribution rule sets - groups multiple plan_contributions rows together
// so an eligibility group can reference one rule set (Shape B). One default
// set per plan is created during backfill; new sets are added when groups
// diverge.
export const contributionRuleSets = pgTable(
  "contribution_rule_sets",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    label: text("label").notNull().default("default"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    planReference: index("contribution_rule_sets_plan_idx").on(table.planId),
  })
);

// Plan Contributions - normalized rows and computed amounts when tied to a rate
export const planContributions = pgTable(
  "plan_contributions",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
    planRateId: integer("plan_rate_id").references(() => planRates.id, { onDelete: "set null" }),
    basePlanId: integer("base_plan_id"),
    contributionRuleSetId: integer("contribution_rule_set_id")
      .notNull()
      .references(() => contributionRuleSets.id, { onDelete: "cascade" }),
    contributionType: text("contribution_type").notNull(), // tier_rates | table_rates | age_rates
    contributionTypeValue: text("contribution_type_value"), // tier key | table label | age band
    payer: text("payer"), // employer | employee | base
    percentage: decimal("percentage", { precision: 5, scale: 2 }),
    nominalAmount: decimal("nominal_amount", { precision: 10, scale: 2 }),
    maxAmount: decimal("max_amount", { precision: 10, scale: 2 }),
    employeeAmount: decimal("employee_amount", { precision: 10, scale: 2 }),
    employerAmount: decimal("employer_amount", { precision: 10, scale: 2 }),
    // { type: "percent_of_plan", basePlanId: number, percentage: number }
    // At fill time the rendered employer amount is max(declared, floor).
    affordabilityFloor: jsonb("affordability_floor"),
  },
  (table) => ({
    planReference: index("plan_contributions_plan_idx").on(table.planId),
    rateReference: index("plan_contributions_rate_idx").on(table.planRateId),
    typeReference: index("plan_contributions_type_idx").on(
      table.contributionType
    ),
    ruleSetReference: index("plan_contributions_rule_set_idx").on(
      table.contributionRuleSetId
    ),
  })
);

export const clientPlans = pgTable("client_plans", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
  effectiveDate: timestamp("effective_date").notNull(),
  endDate: timestamp("end_date"),
  enrolledEmployees: integer("enrolled_employees").notNull(),
  totalPremium: decimal("total_premium", { precision: 10, scale: 2 }).notNull(),
  renewalDate: timestamp("renewal_date"),
  renewalMonth: integer("renewal_month"),
  status: text("status").default("active"),
  isActive: boolean("is_active").default(true),
  // Carrier-issued group/policy number for THIS employer on THIS plan. Lives
  // on the link (per client), never on the shared plan row — editing one
  // client's group number must not change any other client's (plans.planCode
  // is the plan's own stable identifier, a different thing).
  groupNumber: text("group_number"),
  // Snapshot of the employer's primary-location BASIS in force at this link's
  // plan-year start (resolution inputs, never resolved rates — spec scenario
  // 08). The group rating area is derived at query time from these + the
  // effective-dated geo map as of the plan-year start, so a mid-year primary-
  // location change or a future-dated geo remap never re-rates the current
  // plan year (scenarios 06/14). Null = legacy link; falls back to the
  // client's current primary address.
  ratingLocationState: varchar("rating_location_state", { length: 10 }),
  ratingLocationZip: varchar("rating_location_zip", { length: 20 }),
  ratingLocationCounty: varchar("rating_location_county", { length: 100 }),
  // Per-client commission override. Null = no override, use the plan's own
  // commissionRate/commissionCalculationType/commissionValue as the default
  // (plans.commissionRate is shared across every client on that plan, so
  // editing it there would change every other client's default too).
  commissionRate: decimal("commission_rate", { precision: 5, scale: 2 }),
  commissionCalculationType: text("commission_calculation_type"),
  commissionValue: decimal("commission_value", { precision: 10, scale: 2 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Renewal record (Workstream B): a renewal closes the current client_plans
// link and creates a NEW link for the next plan year on the SAME library plan
// (no plan-row clone). This table captures what the roll-forward changed —
// old/new group rating area (effective-dated geo remaps reach an employer only
// at its renewal, scenario 06) and old/new resolved rate version (a mid-cycle
// publish reaches an employer only at its own renewal, scenario 15).
export const clientPlanRenewals = pgTable(
  "client_plan_renewals",
  {
    id: serial("id").primaryKey(),
    clientPlanId: integer("client_plan_id")
      .notNull()
      .references(() => clientPlans.id, { onDelete: "cascade" }),
    newClientPlanId: integer("new_client_plan_id").references(() => clientPlans.id, {
      onDelete: "set null",
    }),
    clientId: integer("client_id")
      .notNull()
      .references(() => clients.id, { onDelete: "cascade" }),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.id, { onDelete: "cascade" }),
    // Set when the renewal moved the employer onto a DIFFERENT plan — the
    // library holds one plan row per plan year, so next year's version of a
    // plan is another row. Null = same plan, new plan year.
    newPlanId: integer("new_plan_id").references(() => plans.id, { onDelete: "set null" }),
    // The broker chose to carry current enrollments into the new plan year.
    carryOverEnrollments: boolean("carry_over_enrollments").notNull().default(false),
    effectiveStart: timestamp("effective_start").notNull(),
    fromRatingAreaId: integer("from_rating_area_id"),
    toRatingAreaId: integer("to_rating_area_id"),
    fromVersionId: integer("from_version_id").references(() => planRateVersions.id, {
      onDelete: "set null",
    }),
    toVersionId: integer("to_version_id").references(() => planRateVersions.id, {
      onDelete: "set null",
    }),
    notes: text("notes"),
    createdById: varchar("created_by_id"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    clientPlanIdx: index("client_plan_renewals_client_plan_idx").on(table.clientPlanId),
    clientIdx: index("client_plan_renewals_client_idx").on(table.clientId, table.planId),
  })
);

export type ClientPlanRenewal = typeof clientPlanRenewals.$inferSelect;
export type InsertClientPlanRenewal = typeof clientPlanRenewals.$inferInsert;

// Plans an agency offers its OWN employees — the agency-side counterpart of
// client_plans. Without this link the only signal was plans.agencyId, which
// marks who CREATED a plan, so the agency's Benefits tab listed every custom
// plan the agency had ever built for any client. agencyId follows the
// plans.agencyId / employees.agencyId convention (broker UUID, no FK).
export const agencyPlans = pgTable(
  "agency_plans",
  {
    id: serial("id").primaryKey(),
    agencyId: text("agency_id").notNull(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    agencyPlanUnique: uniqueIndex("agency_plans_agency_plan_uq").on(table.agencyId, table.planId),
  })
);

export type AgencyPlan = typeof agencyPlans.$inferSelect;

// Plan documents: SBCs and other plan-level attachments — an uploaded file OR
// an external link (exactly one of filePath/externalUrl). Employees enrolled
// through a linked client can download them.
export const planDocuments = pgTable(
  "plan_documents",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    docType: text("doc_type").notNull().default("other"), // sbc | summary | other
    filePath: text("file_path"),
    fileName: text("file_name"),
    mimeType: text("mime_type"),
    externalUrl: text("external_url"),
    uploadedById: varchar("uploaded_by_id"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    planIdx: index("plan_documents_plan_idx").on(table.planId),
  })
);

export type PlanDocument = typeof planDocuments.$inferSelect;

// Client+Plan eligibility groups. A row IS an eligibility group: filters
// (states, locations, classes, employment types, min hours) plus full
// eligibility settings (waiting period, dependents, termination, tax
// determination, etc.) plus a contribution rule set. Multi-group is enabled
// by allowing N rows per (clientId, planId); existing single-row plans
// backfill as one default group with priority 0.
//
// DEV-148: a plan is now plan IDENTITY (name, type, network). All eligibility
// config lives on the group so groups can fully override each other.
export const clientPlanEligibility = pgTable("client_plan_eligibility", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id")
    .notNull()
    .references(() => clients.id, { onDelete: "cascade" }),
  planId: integer("plan_id")
    .notNull()
    .references(() => plans.id, { onDelete: "cascade" }),
  name: text("name"),
  // Higher number wins on overlap. Backfilled rows = 0 so any new, more
  // specific group naturally overrides the default.
  priority: integer("priority").notNull().default(0),
  minHoursPerWeek: integer("min_hours_per_week"),
  specialEnrollmentEvents: text("special_enrollment_events").array(),
  // Filter dimensions
  allowedStates: text("allowed_states").array(),
  allowedLocationIds: integer("allowed_location_ids").array(),
  classIds: integer("class_ids").array(),
  employmentTypes: text("employment_types").array(),
  // Full eligibility settings (formerly plan-level). Backfilled from plans
  // on migration 0048; new groups inherit from primary on POST.
  waitingPeriod: integer("waiting_period"),
  waitingPeriodType: text("waiting_period_type"),
  entryDate: text("entry_date"),
  taxDetermination: text("tax_determination"),
  domesticPartnerTaxDetermination: text("domestic_partner_tax_determination"),
  spouseEligible: boolean("spouse_eligible"),
  spouseMinAge: integer("spouse_min_age"),
  spouseMaxAge: integer("spouse_max_age"),
  childrenEligible: boolean("children_eligible"),
  childrenMinAge: integer("children_min_age"),
  childrenMaxAge: integer("children_max_age"),
  childrenStudentMaxAge: integer("children_student_max_age"),
  childrenDisabledMaxAge: integer("children_disabled_max_age"),
  childrenTermination: text("children_termination"),
  terminationTiming: text("termination_timing"),
  enrollmentRequired: boolean("enrollment_required"),
  // Each group must point at a contribution rule set (groups can share a set).
  contributionRuleSetId: integer("contribution_rule_set_id")
    .notNull()
    .references(() => contributionRuleSets.id, { onDelete: "restrict" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Readability alias - a clientPlanEligibility row IS an eligibility group.
// Existing code keeps using ClientPlanEligibility; new code can use EligibilityGroup.
export type EligibilityGroup = typeof clientPlanEligibility.$inferSelect;

// Client+Plan ineligible employees
export const clientPlanIneligibleEmployees = pgTable(
  "client_plan_ineligible_employees",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    planId: integer("plan_id").notNull(),
    employeeId: integer("employee_id").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  }
);

// Utility arrays for frontend use
export const PLAN_TYPES = Object.values(PlanType);
export const NETWORK_TYPES = Object.values(NetworkType);
export const METAL_TIERS = Object.values(MetalTier);
export const WAITING_PERIOD_TYPES = Object.values(WaitingPeriodType);
export const CHILDREN_TERMINATION_OPTIONS = Object.values(ChildrenTermination);
export const TERMINATION_TIMING_OPTIONS = Object.values(TerminationTiming);
export const ENTRY_DATE_OPTIONS = Object.values(EntryDate);
export const TAX_DETERMINATION_OPTIONS = Object.values(TaxDetermination);
export const EMPLOYMENT_TYPES = Object.values(EmploymentType);
export const CONTINGENCY_TYPES = Object.values(ContingencyType);
export const BENEFIT_TYPES = Object.values(BenefitType);
export const COVERAGE_TIERS = Object.values(CoverageTier);
export const PLAN_FEATURE_TYPES = Object.values(PlanFeatureType);

// NEW CLEAN SYSTEM EXPORTS
export const RATE_STRUCTURE_TYPES = Object.values(RateStructureType);
export const AGE_BAND_TYPES = Object.values(AgeBandType);

// Utility arrays with labels for frontend dropdowns
export const PLAN_TYPE_OPTIONS = [
  { value: PlanType.HEALTH, label: "Health Insurance" },
  { value: PlanType.MEDICAL, label: "Medical Insurance" },
  { value: PlanType.DENTAL, label: "Dental Insurance" },
  { value: PlanType.VISION, label: "Vision Insurance" },
  { value: PlanType.LIFE, label: "Life Insurance" },
  { value: PlanType.VOLUNTARY_LIFE, label: "Voluntary Life" },
  { value: PlanType.ADD, label: "AD&D" },
  { value: PlanType.STD, label: "Short-Term Disability (STD)" },
  { value: PlanType.LTD, label: "Long-Term Disability (LTD)" },
  { value: PlanType.ACCIDENT, label: "Accident" },
  { value: PlanType.CRITICAL_ILLNESS, label: "Critical Illness" },
  { value: PlanType.CANCER, label: "Cancer" },
  { value: PlanType.HOSPITAL_INDEMNITY, label: "Hospital Indemnity" },
  { value: PlanType.TELEHEALTH, label: "Telehealth" },
  { value: PlanType.LEGAL, label: "Legal" },
  { value: PlanType.ID_THEFT, label: "ID Theft Protection" },
  { value: PlanType.FSA_HEALTH, label: "FSA — Health Care" },
  { value: PlanType.FSA_DEPENDENT_CARE, label: "FSA — Dependent Care" },
  { value: PlanType.PET, label: "Pet Insurance" },
  { value: PlanType.TRANSIT, label: "Transit / Commuter" },
  { value: PlanType.CHIROPRACTIC, label: "Chiropractic" },
  { value: PlanType.ACUPUNCTURE, label: "Acupuncture" },
  { value: PlanType.EAP, label: "EAP" },
];

export const NETWORK_TYPE_OPTIONS = [
  { value: NetworkType.HMO, label: "Health Maintenance Organization (HMO)" },
  { value: NetworkType.PPO, label: "Preferred Provider Organization (PPO)" },
  { value: NetworkType.EPO, label: "Exclusive Provider Organization (EPO)" },
  { value: NetworkType.POS, label: "Point of Service (POS)" },
  { value: NetworkType.HDHP, label: "High Deductible Health Plan (HDHP)" },
];

export const METAL_TIER_OPTIONS = [
  { value: MetalTier.BRONZE, label: "Bronze" },
  { value: MetalTier.SILVER, label: "Silver" },
  { value: MetalTier.GOLD, label: "Gold" },
  { value: MetalTier.PLATINUM, label: "Platinum" },
];

export const WAITING_PERIOD_TYPE_OPTIONS = [
  { value: WaitingPeriodType.DAYS, label: "Days" },
  { value: WaitingPeriodType.MONTHS, label: "Months" },
];

export const ENTRY_DATE_OPTIONS_WITH_LABELS = [
  { value: EntryDate.IMMEDIATE, label: "Immediate" },
  { value: EntryDate.FIRST_OF_MONTH, label: "First of the Month" },
  {
    value: EntryDate.FIRST_OF_FOLLOWING_MONTH,
    label: "First of the Following Month",
  },
  { value: EntryDate.FIFTEENTH_OF_MONTH, label: "15th of the Month" },
];

export const TERMINATION_TIMING_OPTIONS_WITH_LABELS = [
  { value: TerminationTiming.IMMEDIATE, label: "Immediate (same day)" },
  { value: TerminationTiming.MID_MONTH, label: "Mid-Month (15th)" },
  { value: TerminationTiming.END_OF_MONTH, label: "End of Month" },
];

export const TAX_DETERMINATION_OPTIONS_WITH_LABELS = [
  { value: TaxDetermination.PRE_TAX, label: "Pre-tax" },
  { value: TaxDetermination.POST_TAX, label: "Post-tax" },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: EmploymentType.FULL_TIME, label: "Full-Time Employee" },
  { value: EmploymentType.PART_TIME, label: "Part-Time Employee" },
  { value: EmploymentType.VARIABLE_HOUR, label: "Variable Hour Employee" },
  { value: EmploymentType.SEASONAL, label: "Seasonal Employee" },
  { value: EmploymentType.CONTRACTOR, label: "Contractor" },
  { value: EmploymentType.INTERN, label: "Intern" },
];

export const CONTINGENCY_TYPE_OPTIONS = [
  { value: ContingencyType.ENROLLED, label: "Enrolled" },
  { value: ContingencyType.ENROLLED_BUNDLED, label: "Enrolled Bundled" },
  {
    value: ContingencyType.ENROLLED_BUNDLED_COBRA,
    label: "Enrolled Bundled (COBRA)",
  },
  { value: ContingencyType.NOT_ENROLLED, label: "Not-Enrolled" },
];

// Additional utility arrays for other enums
export const BENEFIT_TYPE_OPTIONS = [
  { value: BenefitType.MEDICAL, label: "Medical" },
  { value: BenefitType.PRESCRIPTION, label: "Prescription Drug" },
  { value: BenefitType.PREVENTIVE, label: "Preventive Care" },
  { value: BenefitType.EMERGENCY, label: "Emergency Services" },
  { value: BenefitType.SPECIALIST, label: "Specialist Care" },
  { value: BenefitType.URGENT_CARE, label: "Urgent Care" },
  { value: BenefitType.HOSPITAL, label: "Hospital Services" },
  { value: BenefitType.MENTAL_HEALTH, label: "Mental Health" },
  { value: BenefitType.SUBSTANCE_ABUSE, label: "Substance Abuse Treatment" },
  { value: BenefitType.MATERNITY, label: "Maternity Care" },
  { value: BenefitType.DENTAL_BASIC, label: "Dental Basic" },
  { value: BenefitType.DENTAL_MAJOR, label: "Dental Major" },
  { value: BenefitType.ORTHODONTIA, label: "Orthodontia" },
  { value: BenefitType.VISION_EXAM, label: "Vision Exam" },
  { value: BenefitType.VISION_FRAMES, label: "Vision Frames" },
  { value: BenefitType.VISION_LENSES, label: "Vision Lenses" },
];

export const COVERAGE_TIER_OPTIONS = [
  { value: CoverageTier.EMPLOYEE_ONLY, label: "Employee Only" },
  { value: CoverageTier.EMPLOYEE_SPOUSE, label: "Employee + Spouse" },
  { value: CoverageTier.EMPLOYEE_CHILDREN, label: "Employee + Child(ren)" },
  { value: CoverageTier.FAMILY, label: "Family" },
];

export const RATE_STRUCTURE_TYPE_OPTIONS = [
  { value: RateStructureType.TIER_RATES, label: "Tier Rates" },
  { value: RateStructureType.AGE_RATES, label: "Age Rates" },
  { value: RateStructureType.AGE_TIER_RATES, label: "Age Band + Tier Rates" },
  { value: RateStructureType.TABLE_RATES, label: "Table Rates" },
];

export const AGE_BAND_TYPE_OPTIONS = [
  { value: AgeBandType.ALL_AGES, label: "All Ages" },
  { value: AgeBandType.FIVE_YEAR, label: "5 Year Age Bands" },
  { value: AgeBandType.TEN_YEAR, label: "10 Year Age Bands" },
];

export const PLAN_FEATURE_TYPE_OPTIONS = [
  { value: PlanFeatureType.WELLNESS, label: "Wellness Programs" },
  { value: PlanFeatureType.HSA_ELIGIBLE, label: "HSA Eligible" },
  { value: PlanFeatureType.TELEHEALTH, label: "Telehealth Services" },
  {
    value: PlanFeatureType.PRESCRIPTION_DRUG,
    label: "Prescription Drug Coverage",
  },
  { value: PlanFeatureType.PREVENTIVE_CARE, label: "Preventive Care" },
  { value: PlanFeatureType.MENTAL_HEALTH, label: "Mental Health Services" },
  { value: PlanFeatureType.MATERNITY, label: "Maternity Care" },
  { value: PlanFeatureType.VISION, label: "Vision Coverage" },
  { value: PlanFeatureType.DENTAL, label: "Dental Coverage" },
  { value: PlanFeatureType.CHIROPRACTIC, label: "Chiropractic Care" },
  { value: PlanFeatureType.ACUPUNCTURE, label: "Acupuncture" },
];

// Insert schemas with enum validation
export const insertPlanSchema = createInsertSchema(plans)
  // reviewStatus moves only through the DEV-388 review endpoints.
  .omit({ id: true, createdAt: true, reviewStatus: true })
  .extend({
    type: z.nativeEnum(PlanType),
    networkType: z.nativeEnum(NetworkType).nullable().optional(),
    metalTier: z.nativeEnum(MetalTier).nullable().optional(),
    waitingPeriodType: z.nativeEnum(WaitingPeriodType).optional(),
    childrenTermination: z.nativeEnum(ChildrenTermination).optional(),
    terminationTiming: z.nativeEnum(TerminationTiming).optional(),
    entryDate: z.nativeEnum(EntryDate).optional(),
    taxDetermination: z.nativeEnum(TaxDetermination).optional(),
    domesticPartnerTaxDetermination: z.nativeEnum(TaxDetermination).optional(),
    contingencyType: z.nativeEnum(ContingencyType).nullable().optional(),
    rateStructureType: z.nativeEnum(RateStructureType).optional(),
    effectiveDate: z.string().optional().or(z.date().optional()),
    endDate: z.string().optional().or(z.date().optional()),
    availableStartDate: z.string().optional().or(z.date().optional()),
    availableEndDate: z.string().optional().or(z.date().optional()),
    renewalDate: z.string().optional().or(z.date().optional()),
    contingencyPlanId: z.number().nullable().optional(),
  });

export const insertPlanRateSchema = createInsertSchema(planRates)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    rateStructureType: z.nativeEnum(RateStructureType),
    ageBandType: z.nativeEnum(AgeBandType).optional(),
    coverageTable: z.string().optional(),
  });

export const insertPlanRateVersionSchema = createInsertSchema(planRateVersions).omit({
  id: true,
  publishedAt: true,
});
export type PlanRateVersion = typeof planRateVersions.$inferSelect;
export type InsertPlanRateVersion = z.infer<typeof insertPlanRateVersionSchema>;

// removed plan_rate_contributions in favor of row-based plan_contributions

export const insertAgeBandSchema = createInsertSchema(ageBands)
  .omit({ id: true, createdAt: true })
  .extend({
    ageBandType: z.nativeEnum(AgeBandType),
  });

export const insertPlanBenefitSchema = createInsertSchema(planBenefits)
  .omit({ id: true })
  .extend({
    benefitType: z.nativeEnum(BenefitType),
  });

export const insertPlanContributionSchema = createInsertSchema(
  planContributions
).omit({ id: true });
export type PlanContribution = typeof planContributions.$inferSelect;
export type InsertPlanContribution = typeof planContributions.$inferInsert;

export const insertContributionRuleSetSchema = createInsertSchema(
  contributionRuleSets
).omit({ id: true, createdAt: true, updatedAt: true });
export type ContributionRuleSet = typeof contributionRuleSets.$inferSelect;
export type InsertContributionRuleSet = typeof contributionRuleSets.$inferInsert;

export const insertClientPlanSchema = createInsertSchema(clientPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Insert schemas for client-plan eligibility tables
export const insertClientPlanEligibilitySchema = createInsertSchema(
  clientPlanEligibility
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertClientPlanIneligibleEmployeeSchema = createInsertSchema(
  clientPlanIneligibleEmployees
).omit({ id: true, createdAt: true });

// Types
export type Plan = typeof plans.$inferSelect;
export type PlanWithCarrier = Plan & { carrier: string | null };
export type InsertPlan = z.infer<typeof insertPlanSchema>;

export type PlanRate = typeof planRates.$inferSelect;
export type InsertPlanRate = z.infer<typeof insertPlanRateSchema>;
// removed PlanRateContribution types

export type AgeBand = typeof ageBands.$inferSelect;
export type InsertAgeBand = z.infer<typeof insertAgeBandSchema>;

export type PlanBenefit = typeof planBenefits.$inferSelect;
export type InsertPlanBenefit = z.infer<typeof insertPlanBenefitSchema>;

export type PlanFeature = typeof planFeatures.$inferSelect;
export type InsertPlanFeature = z.infer<typeof insertPlanFeatureSchema>;

// Life/Disability Types
export type LifeDisabilityCoverageConfig = typeof lifeDisabilityCoverageConfig.$inferSelect;
export type InsertLifeDisabilityCoverageConfig = z.infer<typeof insertLifeDisabilityCoverageConfigSchema>;

export type EvidenceOfInsurability = typeof evidenceOfInsurability.$inferSelect;
export type InsertEvidenceOfInsurability = z.infer<typeof insertEvidenceOfInsurabilitySchema>;

export type LifeInsuranceBeneficiary = typeof lifeInsuranceBeneficiaries.$inferSelect;
export type InsertLifeInsuranceBeneficiary = z.infer<typeof insertLifeInsuranceBeneficiarySchema>;

export type ClientPlan = typeof clientPlans.$inferSelect;
export type InsertClientPlan = z.infer<typeof insertClientPlanSchema>;

export type ClientPlanEligibility = typeof clientPlanEligibility.$inferSelect;
export type InsertClientPlanEligibility = z.infer<
  typeof insertClientPlanEligibilitySchema
>;

export type ClientPlanIneligibleEmployee =
  typeof clientPlanIneligibleEmployees.$inferSelect;
export type InsertClientPlanIneligibleEmployee = z.infer<
  typeof insertClientPlanIneligibleEmployeeSchema
>;

// Plan Features and Wellness Programs
export const planFeatures = pgTable("plan_features", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "cascade" }),
  featureType: text("feature_type").notNull(), // wellness, hsa_eligible, telehealth, prescription_drug
  featureName: text("feature_name").notNull(),
  description: text("description"),
  isIncluded: boolean("is_included").default(true),
  additionalCost: decimal("additional_cost", { precision: 10, scale: 2 }),
});

// Life and Disability Coverage Configuration Table
// This table extends plans with Life/Disability specific configuration
// Following Open/Closed Principle - new table instead of modifying plans table
export const lifeDisabilityCoverageConfig = pgTable("life_disability_coverage_config", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().unique(), // references plans.id - one config per plan

  // Coverage Amount Configuration
  coverageAmountType: text("coverage_amount_type").notNull(), // flat_amount, earnings_multiple, earnings_percentage
  flatCoverageAmount: decimal("flat_coverage_amount", { precision: 12, scale: 2 }), // For flat amount type
  earningsMultiple: decimal("earnings_multiple", { precision: 5, scale: 2 }), // For earnings multiple (e.g., 2x, 3x, 5x)
  earningsPercentage: decimal("earnings_percentage", { precision: 5, scale: 2 }), // For percentage (e.g., 60%, 66.67%)
  earningsBase: text("earnings_base"), // base_salary, base_plus_bonus, total_compensation, w2_earnings

  // Coverage Limits
  minimumCoverage: decimal("minimum_coverage", { precision: 12, scale: 2 }), // Minimum coverage amount
  maximumCoverage: decimal("maximum_coverage", { precision: 12, scale: 2 }), // Maximum coverage amount
  coverageIncrement: decimal("coverage_increment", { precision: 12, scale: 2 }), // Increment units (e.g., $1000, $10000)

  // Rounding Rules
  roundingRule: text("rounding_rule").default("none"), // none, nearest_thousand, round_up_thousand, custom, etc.
  roundToNearest: decimal("round_to_nearest", { precision: 12, scale: 2 }), // Custom rounding amount
  roundGuaranteedIssue: boolean("round_guaranteed_issue").default(false),
  roundAgeReduction: boolean("round_age_reduction").default(false),

  // Age Reduction Settings
  reductionTiming: text("reduction_timing"), // birthday, plan_anniversary, first_of_birthday_month, first_of_month_following_birthday, first_of_year
  spouseAgeBase: text("spouse_age_base"), // employee, spouse, na

  // Guaranteed Issue (GI) Configuration
  guaranteedIssueAmount: decimal("guaranteed_issue_amount", { precision: 12, scale: 2 }), // Max without EOI
  guaranteedIssueNewHire: decimal("guaranteed_issue_new_hire", { precision: 12, scale: 2 }), // GI for newly eligible
  guaranteedIssueOpenEnrollment: decimal("guaranteed_issue_open_enrollment", { precision: 12, scale: 2 }), // GI during OE
  eoiRequired: boolean("eoi_required").default(false), // Whether EOI is ever required
  eoiCarrierPortalUrl: text("eoi_carrier_portal_url"), // URL to carrier's EOI system
  increaseAllowance: decimal("increase_allowance", { precision: 12, scale: 2 }), // Increase allowance at OE
  maximumWithoutEoi: decimal("maximum_without_eoi", { precision: 12, scale: 2 }), // Max coverage without EOI
  alwaysRequireEoiAtOe: boolean("always_require_eoi_at_oe").default(false), // Always require EOI at OE
  eoiRequiredAfterAmount: decimal("eoi_required_after_amount", { precision: 12, scale: 2 }), // EOI required after this amount

  // Disability-Specific Fields (informational)
  eliminationPeriod: text("elimination_period"), // 0_days, 7_days, 30_days, 60_days, 90_days, 180_days
  maximumBenefitPeriod: text("maximum_benefit_period"), // 2_years, 5_years, to_age_65, to_age_67, lifetime
  disabilityDefinition: text("disability_definition"), // own_occupation, modified_own_occupation, any_occupation
  maxMonthlyBenefit: decimal("max_monthly_benefit", { precision: 10, scale: 2 }), // Max monthly payout
  benefitSchedule: text("benefit_schedule"), // "weekly" | "monthly"

  // Per-Unit Rate Configuration (for Life/Disability)
  ratePerUnit: decimal("rate_per_unit", { precision: 10, scale: 4 }), // Rate per $1,000 coverage
  rateUnitBasis: decimal("rate_unit_basis", { precision: 10, scale: 2 }).default('1000'), // Basis amount (typically $1000 or $100)

  // Rate Structure
  rateType: text("rate_type").default("composite"), // "composite" | "tiered"
  rateBasis: text("rate_basis").default("benefit_amount"), // "benefit_amount" | "earnings_amount"
  tobaccoDistinction: boolean("tobacco_distinction").default(false),
  tobaccoRate: decimal("tobacco_rate", { precision: 10, scale: 4 }),

  // Additional Settings
  portabilityAllowed: boolean("portability_allowed").default(false), // Can employee keep coverage after termination
  conversionAllowed: boolean("conversion_allowed").default(false), // Can convert to individual policy
  acceleratedDeathBenefit: boolean("accelerated_death_benefit").default(false), // For life insurance

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Evidence of Insurability (EOI) Tracking Table
export const evidenceOfInsurability = pgTable("evidence_of_insurability", {
  id: serial("id").primaryKey(),
  enrollmentId: integer("enrollment_id").notNull(), // references enrollments.id
  employeeId: integer("employee_id").notNull(), // references employees.id
  planId: integer("plan_id").notNull(), // references plans.id

  requestedCoverageAmount: decimal("requested_coverage_amount", { precision: 12, scale: 2 }).notNull(),
  guaranteedIssueAmount: decimal("guaranteed_issue_amount", { precision: 12, scale: 2 }),
  amountRequiringEOI: decimal("amount_requiring_eoi", { precision: 12, scale: 2 }), // Amount over GI

  eoiStatus: text("eoi_status").notNull().default("pending"), // pending, submitted, approved, denied, conditionally_approved
  eligibilityWindow: text("eligibility_window"), // newly_eligible, open_enrollment, qualifying_event, late_entrant

  submissionDate: timestamp("submission_date"),
  approvalDate: timestamp("approval_date"),
  denialDate: timestamp("denial_date"),
  denialReason: text("denial_reason"),

  approvedCoverageAmount: decimal("approved_coverage_amount", { precision: 12, scale: 2 }),
  exclusionsOrLimitations: text("exclusions_or_limitations"), // Any conditions on approval

  carrierReferenceNumber: text("carrier_reference_number"), // Carrier's tracking ID
  carrierPortalUrl: text("carrier_portal_url"), // Link to carrier's EOI portal

  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Life Insurance Beneficiaries Table
export const lifeInsuranceBeneficiaries = pgTable("life_insurance_beneficiaries", {
  id: serial("id").primaryKey(),
  enrollmentId: integer("enrollment_id").notNull(), // references enrollments.id
  employeeId: integer("employee_id").notNull(), // references employees.id
  planId: integer("plan_id").notNull(), // references plans.id

  beneficiaryType: text("beneficiary_type").notNull(), // primary, contingent
  sequenceOrder: integer("sequence_order").notNull(), // Order within type (1, 2, 3...)

  firstName: text("first_name").notNull(),
  middleName: text("middle_name"),
  lastName: text("last_name").notNull(),
  relationship: text("relationship"), // spouse, child, parent, sibling, other

  percentage: decimal("percentage", { precision: 5, scale: 2 }).notNull(), // Percentage of benefit (must total 100%)

  dateOfBirth: date("date_of_birth"),
  ssn: text("ssn"), // For claims processing

  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),

  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  enrollmentIdx: index("beneficiary_enrollment_idx").on(table.enrollmentId),
  employeeIdx: index("beneficiary_employee_idx").on(table.employeeId),
}));

export const insertPlanFeatureSchema = createInsertSchema(planFeatures).omit({
  id: true,
});

// Life/Disability Insert Schemas
export const insertLifeDisabilityCoverageConfigSchema = createInsertSchema(lifeDisabilityCoverageConfig)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    coverageAmountType: z.nativeEnum(CoverageAmountType),
    earningsBase: z.nativeEnum(EarningsBase).optional().nullable(),
    roundingRule: z.nativeEnum(RoundingRule).optional().nullable(),
    roundGuaranteedIssue: z.boolean().nullish().transform((v) => v ?? false),
    roundAgeReduction: z.boolean().nullish().transform((v) => v ?? false),
    eliminationPeriod: z.nativeEnum(EliminationPeriod).optional().nullable(),
    maximumBenefitPeriod: z.nativeEnum(DisabilityBenefitPeriod).optional().nullable(),
    disabilityDefinition: z.nativeEnum(DisabilityDefinition).optional().nullable(),
  });

export const insertEvidenceOfInsurabilitySchema = createInsertSchema(evidenceOfInsurability)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    eoiStatus: z.nativeEnum(EOIStatus),
    eligibilityWindow: z.nativeEnum(EligibilityWindow).optional(),
  });

export const insertLifeInsuranceBeneficiarySchema = createInsertSchema(lifeInsuranceBeneficiaries)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    percentage: z.coerce.number().min(0).max(100),
  });

// ── Tables present in dev but removed in feature/josh-3 merge ─────────────

export const ageReductionSchedules = pgTable("age_reduction_schedules", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull(),
  ageThreshold: integer("age_threshold").notNull(),
  reductionPercentage: decimal("reduction_percentage", { precision: 5, scale: 2 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  planIdx: index("age_reduction_plan_idx").on(table.planId),
}));

export const fsaPlanConfig = pgTable("fsa_plan_config", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().unique(),
  fsaType: text("fsa_type").notNull(),
  annualElectionMin: decimal("annual_election_min", { precision: 10, scale: 2 }),
  annualElectionMax: decimal("annual_election_max", { precision: 10, scale: 2 }),
  /** Fixed annual employer contribution. Combined with the employee election
   *  must not exceed annualElectionMax. */
  employerContribution: decimal("employer_contribution", { precision: 10, scale: 2 }),
  gracePeriodDays: integer("grace_period_days").default(0),
  runOutPeriodDays: integer("run_out_period_days"),
  carryoverAllowed: boolean("carryover_allowed").default(false),
  carryoverMax: decimal("carryover_max", { precision: 10, scale: 2 }),
  prorationMethod: text("proration_method").default("none"),
  midYearChangeAllowed: boolean("mid_year_change_allowed").default(false),
  claimFilingDeadlineDays: integer("claim_filing_deadline_days"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const supplementalPlanConfig = pgTable("supplemental_plan_config", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id").notNull().unique(),
  benefitScheduleType: text("benefit_schedule_type"),
  coverageAmountType: text("coverage_amount_type"),
  flatCoverageAmount: decimal("flat_coverage_amount", { precision: 12, scale: 2 }),
  earningsMultiple: decimal("earnings_multiple", { precision: 5, scale: 2 }),
  earningsBase: text("earnings_base"),
  minimumCoverage: decimal("minimum_coverage", { precision: 12, scale: 2 }),
  maximumCoverage: decimal("maximum_coverage", { precision: 12, scale: 2 }),
  coverageIncrement: decimal("coverage_increment", { precision: 12, scale: 2 }),
  guaranteedIssueAmount: decimal("guaranteed_issue_amount", { precision: 12, scale: 2 }),
  portabilityAllowed: boolean("portability_allowed").default(false),
  conversionAllowed: boolean("conversion_allowed").default(false),
  ratePerUnit: decimal("rate_per_unit", { precision: 10, scale: 4 }),
  rateUnitBasis: decimal("rate_unit_basis", { precision: 10, scale: 2 }).default('1000'),
  roundingRule: text("rounding_rule").default("none"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAgeReductionScheduleSchema = createInsertSchema(ageReductionSchedules).omit({ id: true, createdAt: true });
export const insertFsaPlanConfigSchema = createInsertSchema(fsaPlanConfig).omit({ id: true, createdAt: true, updatedAt: true });
export const insertSupplementalPlanConfigSchema = createInsertSchema(supplementalPlanConfig).omit({ id: true, createdAt: true, updatedAt: true });

export type AgeReductionSchedule = typeof ageReductionSchedules.$inferSelect;
export type InsertAgeReductionSchedule = z.infer<typeof insertAgeReductionScheduleSchema>;
export type FsaPlanConfig = typeof fsaPlanConfig.$inferSelect;
export type InsertFsaPlanConfig = z.infer<typeof insertFsaPlanConfigSchema>;
export type SupplementalPlanConfig = typeof supplementalPlanConfig.$inferSelect;
export type InsertSupplementalPlanConfig = z.infer<typeof insertSupplementalPlanConfigSchema>;
